import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { ReplenishmentRaiseTarget } from "@shared-core/data/adapter";
import type {
  ConfirmReplenishmentProposalRequest,
  PreviewedRaiseItem,
  RaisePreview,
  ReplenishmentLastOrder,
  ReplenishNowRequest,
} from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import {
  formatCount,
  formatDateOrDash,
  formatQuotedPrice,
} from "@shared-core/domain/formatters";
import {
  PRICE_SOURCE_LABELS,
  RAISES_AS_LABELS,
  expectedArrivalLabel,
} from "@shared-core/domain/stock-replenishment";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { Badge } from "../../app/components/primitives/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../app/components/primitives/card";

export type RaisePreviewState =
  | { status: "loading"; preview: RaisePreview | null }
  | { status: "ready"; preview: RaisePreview }
  | { status: "error"; preview: RaisePreview | null };

/**
 * What a raise would do for this request, refreshed (after a short pause) whenever the request changes. `request`
 * null means there is nothing to preview yet (a blank quantity): the last answer stays. The answer is the
 * backend's; blockers and what each item still needs come back inside it, field-tagged.
 */
export function useRaisePreview(
  target: ReplenishmentRaiseTarget,
  request: ConfirmReplenishmentProposalRequest | ReplenishNowRequest | null,
): RaisePreviewState {
  const dataAdapter = useDataAdapter();
  const key = request == null ? null : JSON.stringify([target, request]);
  const settled = useDebouncedValue(key, 400);
  const [state, setState] = useState<RaisePreviewState>({
    status: "loading",
    preview: null,
  });

  useEffect(() => {
    if (settled == null) return;
    let cancelled = false;
    const [settledTarget, settledRequest] = JSON.parse(settled) as [
      ReplenishmentRaiseTarget,
      ConfirmReplenishmentProposalRequest | ReplenishNowRequest,
    ];
    setState((current) => ({ status: "loading", preview: current.preview }));
    dataAdapter
      .previewReplenishmentRaise(settledTarget, settledRequest)
      .then((preview) => {
        if (!cancelled) setState({ status: "ready", preview });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("stock.replenishment.preview_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
        setState((current) => ({ status: "error", preview: current.preview }));
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, settled]);

  return state;
}

/** "What a raise would do": per item the supplier, cost, price, last price, lead time and anything that stops it. */
export function RaisePreviewCard({
  state,
  lastOrder,
}: {
  state: RaisePreviewState;
  /** What the owner last paid for the first item, when the page has it. */
  lastOrder?: ReplenishmentLastOrder | null;
}) {
  const { preview } = state;
  return (
    <Card aria-busy={state.status === "loading"}>
      <CardHeader className="flex flex-row items-start justify-between gap-4 flex-wrap">
        <CardTitle>What a raise would do</CardTitle>
        <p className="text-xs text-muted-foreground">
          Staff only. Costs are cached supplier costs; the live price is checked
          again when the order is placed.
        </p>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {state.status === "error" && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-red-700 dark:text-red-300"
          >
            <AlertTriangle className="size-4 shrink-0 mt-0.5" />
            Couldn't work out what a raise would do. Change the quantity to try
            again, or reload the page.
          </div>
        )}
        {preview == null ? (
          state.status === "loading" && (
            <p className="text-muted-foreground">
              Working out the supplier and price…
            </p>
          )
        ) : (
          <>
            {preview.items.map((item, index) => (
              <PreviewedItem
                key={item.stock_item_id}
                item={item}
                showCode={preview.items.length > 1}
                lastOrder={index === 0 ? lastOrder : null}
              />
            ))}
            {preview.items.every((item) => item.can_raise) && (
              <p className="text-muted-foreground">
                Raised as{" "}
                <span className="font-medium text-foreground">
                  {preview.order_kind
                    ? RAISES_AS_LABELS[preview.order_kind]
                    : "an order"}
                </span>
                {preview.pays_on_account
                  ? ", paid by credit account."
                  : ", never invoiced."}
                {preview.is_dry_run &&
                  " Dry run: confirming records this and orders nothing."}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PreviewedItem({
  item,
  showCode,
  lastOrder,
}: {
  item: PreviewedRaiseItem;
  showCode: boolean;
  lastOrder?: ReplenishmentLastOrder | null;
}) {
  const hasSourcing = item.cost_pence != null;
  return (
    <section className="space-y-2" aria-label={`Raise of ${item.stock_code}`}>
      {showCode && (
        <h3 className="font-medium">
          {item.stock_code}
          <span className="ml-2 font-normal text-muted-foreground tabular-nums">
            {formatCount(item.quantity ?? 0)}
          </span>
        </h3>
      )}
      {item.blockers.length > 0 && (
        <ul className="space-y-1 text-red-700 dark:text-red-300">
          {item.blockers.map((blocker) => (
            <li key={`${blocker.field}-${blocker.code}`}>{blocker.message}</li>
          ))}
        </ul>
      )}
      {item.sourcing_unavailable && (
        <p className="text-muted-foreground">
          Supplier options couldn't be read just now. Try again in a minute.
        </p>
      )}
      {item.needs_reason != null &&
        item.cost_pence == null &&
        item.needs_sentence && (
          <p className="text-muted-foreground">{item.needs_sentence}</p>
        )}
      {item.unavailable_routes.length > 0 && (
        <table className="w-full text-left">
          <thead>
            <tr className="text-xs text-muted-foreground">
              <th className="py-1 pr-3 font-normal">Route</th>
              <th className="py-1 font-normal">Why not</th>
            </tr>
          </thead>
          <tbody>
            {item.unavailable_routes.map((route) => (
              <tr key={route.route_code} className="border-t border-border">
                <td className="py-1.5 pr-3">{route.route_label}</td>
                <td className="py-1.5 text-muted-foreground">{route.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {hasSourcing && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-lg border border-dashed border-border bg-surface p-3">
          <dt className="text-xs text-muted-foreground">Supplier</dt>
          <dd>
            {[
              item.supplier_name ?? item.supplier_id,
              item.route_label ?? item.route_code,
              item.turnaround_tier,
            ]
              .filter(Boolean)
              .join(" · ")}
          </dd>
          <dt className="text-xs text-muted-foreground">Cost to us</dt>
          <dd className="tabular-nums">
            {formatQuotedPrice(item.cost_pence!)}{" "}
            <span className="text-xs text-muted-foreground">
              ({formatCount(item.quantity ?? 0)})
            </span>
          </dd>
          <dt className="text-xs text-muted-foreground">
            Price to the customer
          </dt>
          <dd className="tabular-nums">
            {item.price_pence == null ? (
              "None: no price group"
            ) : (
              <>
                {formatQuotedPrice(item.price_pence)} ex VAT{" "}
                {item.price_source && (
                  <span className="text-xs text-muted-foreground">
                    {PRICE_SOURCE_LABELS[item.price_source]}
                  </span>
                )}
              </>
            )}
          </dd>
          {lastOrder && (
            <>
              <dt className="text-xs text-muted-foreground">Last price paid</dt>
              <dd className="tabular-nums">
                {formatQuotedPrice(lastOrder.price_pence)} for{" "}
                {formatCount(lastOrder.quantity)} on {lastOrder.line_ref},{" "}
                {formatDateOrDash(lastOrder.ordered_at)}
              </dd>
            </>
          )}
          {item.lead_days != null && (
            <>
              <dt className="text-xs text-muted-foreground">Lead time</dt>
              <dd>
                {item.lead_days} working day{item.lead_days === 1 ? "" : "s"} to
                the supplier's despatch
                {item.indicative_arrival_on && (
                  <>
                    {" "}
                    · arrives{" "}
                    {expectedArrivalLabel(
                      item.indicative_arrival_on,
                      "assumed",
                    )}{" "}
                    <Badge variant="neutral">indicative</Badge>
                  </>
                )}
              </dd>
            </>
          )}
        </dl>
      )}
    </section>
  );
}
